// The palette the Theme section shows. `TOKEN_LABELS` maps each theme token
// (the CSS custom property) to its semantic name and a short note of what it
// highlights, so the section reads as a design system rather than a list of
// variables; `PALETTE` orders the tokens into groups. Adding a token is one
// entry in each; the section reads its value at runtime.
//
// The section shows only colours that make sense to change one by one, so
// they can become customisable later. `REFERENCE_TOKENS` keeps the rest
// labelled but unshown: the neutrals are a ramp, and the chart neutrals are
// derived from the text colour, so both would be customised as a whole, not
// one by one.

export interface TokenLabel {
  /** What the colour means, e.g. "Win". */
  name: string;
  /** What it is used for, one short line, e.g. "a won game, a gain". */
  use: string;
  /**
   * The token(s) this colour is defined from in core-theme's colors.css:
   * one when it is that token (`var(--viz-win)`), two when it is a mix of
   * them (`color-mix(… var(--viz-win) …, var(--viz-draw))`). Absent when the
   * colour is a value of its own.
   */
  follows?: string[];
}

const NEUTRALS = 'light to dark greys for text and surfaces';
const TIME_CLASS = 'games at that time control';

export const TOKEN_LABELS: Record<string, TokenLabel> = {
  '--color-bg': { name: 'Background', use: 'the page behind everything' },
  '--color-surface': { name: 'Surface', use: 'cards and panels' },
  '--color-text': { name: 'Text', use: 'body text' },
  '--color-accent': { name: 'Accent', use: 'links, the selected item, focus' },
  '--color-accent-2': { name: 'Second accent', use: 'a quieter companion to the accent, for tags' },
  '--color-divider': { name: 'Divider', use: 'hairlines between parts' },

  '--color-neutral-100': { name: 'Neutral 100', use: NEUTRALS },
  '--color-neutral-200': { name: 'Neutral 200', use: NEUTRALS },
  '--color-neutral-300': { name: 'Neutral 300', use: NEUTRALS },
  '--color-neutral-400': { name: 'Neutral 400', use: NEUTRALS },
  '--color-neutral-500': { name: 'Neutral 500', use: NEUTRALS },
  '--color-neutral-600': { name: 'Neutral 600', use: NEUTRALS },
  '--color-neutral-700': { name: 'Neutral 700', use: NEUTRALS },
  '--color-neutral-800': { name: 'Neutral 800', use: NEUTRALS },
  '--color-neutral-900': { name: 'Neutral 900', use: NEUTRALS },

  '--viz-win': { name: 'Win', use: 'a won game, a gain' },
  '--viz-draw': { name: 'Draw', use: 'a drawn game, level' },
  '--viz-loss': { name: 'Loss', use: 'a lost game, a loss' },

  '--viz-score-gain': { name: 'Score gained', use: 'a positive score change', follows: ['--viz-win'] },
  '--viz-score-loss': { name: 'Score lost', use: 'a negative score change', follows: ['--viz-loss'] },
  '--viz-score-level': { name: 'Score level', use: 'no change in the score', follows: ['--viz-ink'] },

  '--viz-grade-best': { name: 'Best move', use: 'the engine’s top choice', follows: ['--viz-win'] },
  '--viz-grade-good': { name: 'Good move', use: 'a sound move, close to the best', follows: ['--viz-win', '--viz-draw'] },
  '--viz-grade-inaccuracy': { name: 'Inaccuracy', use: 'a move that gives a little away', follows: ['--viz-draw'] },
  '--viz-grade-mistake': { name: 'Mistake', use: 'a move that gives a lot away', follows: ['--viz-loss', '--viz-draw'] },
  '--viz-grade-blunder': { name: 'Blunder', use: 'a move that throws the game away', follows: ['--viz-loss'] },
  '--viz-grade-miss': { name: 'Miss', use: 'a winning chance not taken', follows: ['--viz-daily'] },

  '--viz-ending-won': { name: 'Won ending', use: 'you reached an ending and won', follows: ['--viz-win'] },
  '--viz-ending-slipped': { name: 'Slipped win', use: 'winning in the ending, and didn’t win' },
  '--viz-ending-missed': { name: 'Missed win', use: 'a winning move in the ending not played' },
  '--viz-ending-time': { name: 'Lost on time', use: 'you lost on time in the ending' },
  '--viz-ending-lost': { name: 'Lost ending', use: 'lost with no winning chance in the ending' },
  '--viz-ending-drawn': { name: 'Drawn ending', use: 'drawn with no winning chance for you', follows: ['--viz-draw'] },
  '--viz-ending-comeback': { name: 'Comeback', use: 'started lost, and you won or drew' },
  '--viz-ending-blown': { name: 'Blown win', use: 'started winning, and you drew or lost', follows: ['--viz-ending-slipped'] },

  '--viz-arrow-best': { name: 'Best move arrow', use: 'the engine’s best move on the board', follows: ['--viz-grade-best'] },
  '--viz-arrow-blunder': { name: 'Blunder arrow', use: 'a played move that was a blunder', follows: ['--viz-grade-blunder'] },
  '--viz-arrow-played': { name: 'Played move arrow', use: 'any other move played in the game', follows: ['--viz-muted'] },

  '--viz-bullet': { name: 'Bullet', use: TIME_CLASS },
  '--viz-blitz': { name: 'Blitz', use: TIME_CLASS },
  '--viz-rapid': { name: 'Rapid', use: TIME_CLASS },
  '--viz-daily': { name: 'Daily', use: TIME_CLASS },

  '--viz-grid': { name: 'Grid', use: 'chart gridlines', follows: ['--color-text'] },
  '--viz-ink': { name: 'Chart ink', use: 'chart text and lines', follows: ['--color-text'] },
  '--viz-muted': { name: 'Chart muted', use: 'secondary chart text', follows: ['--color-neutral-500'] },
};

export interface PaletteGroup {
  title: string;
  /** Token names, in order; each is looked up in `TOKEN_LABELS`. */
  tokens: string[];
}

export const PALETTE: PaletteGroup[] = [
  { title: 'Base', tokens: ['--color-bg', '--color-surface', '--color-text', '--color-accent', '--color-accent-2', '--color-divider'] },
  { title: 'Results', tokens: ['--viz-win', '--viz-draw', '--viz-loss'] },
  { title: 'Score change', tokens: ['--viz-score-gain', '--viz-score-loss', '--viz-score-level'] },
  {
    title: 'Move grades',
    tokens: ['--viz-grade-best', '--viz-grade-good', '--viz-grade-inaccuracy', '--viz-grade-mistake', '--viz-grade-blunder', '--viz-grade-miss'],
  },
  {
    title: 'Ending cases',
    tokens: [
      '--viz-ending-won',
      '--viz-ending-slipped',
      '--viz-ending-missed',
      '--viz-ending-time',
      '--viz-ending-lost',
      '--viz-ending-drawn',
      '--viz-ending-comeback',
      '--viz-ending-blown',
    ],
  },
  { title: 'Board arrows', tokens: ['--viz-arrow-best', '--viz-arrow-blunder', '--viz-arrow-played'] },
  { title: 'Time classes', tokens: ['--viz-bullet', '--viz-blitz', '--viz-rapid', '--viz-daily'] },
];

/**
 * Labelled tokens the section does not show, kept for reference: the
 * neutrals are a ramp and the chart neutrals derive from the text colour,
 * so they would be customised as a whole later, not one by one.
 */
export const REFERENCE_TOKENS: PaletteGroup[] = [
  {
    title: 'Neutrals',
    tokens: [
      '--color-neutral-100',
      '--color-neutral-200',
      '--color-neutral-300',
      '--color-neutral-400',
      '--color-neutral-500',
      '--color-neutral-600',
      '--color-neutral-700',
      '--color-neutral-800',
      '--color-neutral-900',
    ],
  },
  { title: 'Chart neutrals', tokens: ['--viz-grid', '--viz-ink', '--viz-muted'] },
];

/** A token with its label and the value it resolves to; '' when it resolves to nothing. */
export interface ResolvedToken {
  token: string;
  name: string;
  use: string;
  value: string;
  /** "follows Win" or "mix of Win and Draw" when the colour derives from others; absent otherwise. */
  derived?: string;
}

export interface ResolvedGroup {
  title: string;
  tokens: ResolvedToken[];
}

/** A token's label; the token itself as the name when the map has none. */
export function labelOf(token: string, labels: Record<string, TokenLabel> = TOKEN_LABELS): TokenLabel {
  return labels[token] ?? { name: token, use: '' };
}

/** How a derived colour reads: "follows Win", "mix of Win and Draw"; '' when it follows nothing. */
export function followsNote(follows: string[] | undefined, labels: Record<string, TokenLabel> = TOKEN_LABELS): string {
  if (!follows || follows.length === 0) return '';
  const names = follows.map((t) => labelOf(t, labels).name);
  if (names.length === 1) return `follows ${names[0]}`;
  return `mix of ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The palette with each token's label and value, as `read` gives it (trimmed). */
export function resolvePalette(
  read: (token: string) => string,
  groups: PaletteGroup[] = PALETTE,
  labels: Record<string, TokenLabel> = TOKEN_LABELS,
): ResolvedGroup[] {
  return groups.map((g) => ({
    title: g.title,
    tokens: g.tokens.map((token) => {
      const { name, use, follows } = labelOf(token, labels);
      const derived = followsNote(follows, labels);
      return { token, name, use, value: read(token).trim(), ...(derived ? { derived } : {}) };
    }),
  }));
}
